import { useMemo } from 'react';
import type { RegularBreakpoints } from '@mui/material/Grid';

export type SectionItemKey = 'general-section' | 'assets-section' | 'findings-section';

export type Item = {
  key: ItemKeys | SectionItemKey;
  title: string;
  subtitle?: string;
  showTitle?: boolean;
  show: boolean;
  type?: 'chart' | 'section-heading';
  colSpan?: RegularBreakpoints;
};

export type ItemKeys =
  | 'general-metrics'
  | 'intervention-cost-by-priority'
  | 'asset-metrics'
  | 'findings-metrics'
  | 'intervention-cost-by-age'
  | 'replacement-cost-by-asset-type'
  | 'replacement-cost-by-manufacturer'
  | 'intervention-cost-and-fci';

export const isSectionHeadingItem = (
  item: Item,
): item is Item & { key: SectionItemKey; type: 'section-heading' } =>
  item.type === 'section-heading';

export const isChartItemKey = (key: ItemKeys | SectionItemKey): key is ItemKeys =>
  !key.endsWith('-section');

const defaultColSpan = {
  xs: 12,
  md: 6,
  xl: 6,
};

const defaultItems: Item[] = [
  {
    key: 'general-section',
    title: 'General',
    show: true,
    type: 'section-heading',
    colSpan: { xs: 12 },
  },
  {
    key: 'general-metrics',
    title: 'General Metrics',
    showTitle: false,
    show: true,
    colSpan: { ...defaultColSpan },
  },
  {
    key: 'intervention-cost-by-priority',
    title: 'Intervention Cost by Priority',
    show: true,
    colSpan: { ...defaultColSpan },
  },
  {
    key: 'assets-section',
    title: 'Assets',
    show: true,
    type: 'section-heading',
    colSpan: { xs: 12 },
  },
  {
    key: 'asset-metrics',
    title: 'Asset Metrics',
    showTitle: false,
    show: true,
    colSpan: { ...defaultColSpan },
  },
  {
    key: 'intervention-cost-by-age',
    title: 'Asset Intervention Cost by Age',
    show: true,
    colSpan: { ...defaultColSpan },
  },
  {
    key: 'replacement-cost-by-asset-type',
    title: 'Replacement Cost by Asset Type',
    show: true,
    colSpan: { ...defaultColSpan },
  },
  {
    key: 'replacement-cost-by-manufacturer',
    title: 'Replacement Cost by Manufacturer',
    show: true,
    colSpan: { ...defaultColSpan },
  },
  {
    key: 'findings-section',
    title: 'Findings',
    show: true,
    type: 'section-heading',
    colSpan: { xs: 12 },
  },
  {
    key: 'findings-metrics',
    title: 'Findings Metrics',
    showTitle: false,
    show: true,
    colSpan: { ...defaultColSpan },
  },
  {
    key: 'intervention-cost-and-fci',
    title: 'Cost & FCI by Intervention Year',
    show: true,
    colSpan: { xs: 12 },
  },
];

const useAssetPlanDashboardItems = () => {
  const items = useMemo(
    () =>
      defaultItems.map((item) => {
        if (item.type !== 'section-heading') return item;

        return {
          ...item,
          subtitle: `Data as of ${new Date().toLocaleDateString()}`,
        };
      }),
    [],
  );
  const visibleItems = useMemo(() => items.filter((item) => item.show), [items]);

  return { items, visibleItems };
};

export default useAssetPlanDashboardItems;
